<template>
  <div ref="rootRef" class="relative">
    <!-- Déclencheur : mon picto + mon nom -->
    <button
      type="button"
      class="flex items-center gap-2 rounded-lg px-1.5 py-1 pa-survol"
      :aria-expanded="open"
      aria-haspopup="menu"
      @click.stop="open = !open"
    >
      <UserAvatar :id="auth.user?.userId" :name="auth.user?.displayName ?? ''" :has-avatar="photoMicrosoft(auth.user?.kind)" size="sm" />
      <span class="hidden text-sm text-slate-600 dark:text-alt-dark-muted sm:inline">{{ auth.user?.displayName }}</span>
      <span aria-hidden="true" class="text-xs text-slate-500 dark:text-alt-dark-muted">▾</span>
    </button>

    <div
      v-if="open"
      role="menu"
      class="absolute right-0 z-50 mt-2 w-72 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-alt-dark-border dark:bg-alt-dark-surface"
    >
      <!-- Identité : nature du compte, puis rôles -->
      <div class="flex items-center gap-3 border-b border-slate-100 p-4 dark:border-alt-dark-border">
        <UserAvatar :id="auth.user?.userId" :name="auth.user?.displayName ?? ''" :has-avatar="photoMicrosoft(auth.user?.kind)" size="md" />
        <div class="min-w-0">
          <p class="truncate font-medium text-space-indigo dark:text-altoneo-50">{{ auth.user?.displayName }}</p>
          <p class="truncate text-xs text-slate-500 dark:text-alt-dark-muted">{{ auth.user?.email }}</p>
          <div class="mt-1 flex flex-wrap gap-1">
            <AltBadge v-if="auth.user" :variant="auth.user.kind === 'staff' ? 'navy' : auth.user.kind === 'client' ? 'gold' : 'info'" size="sm">
              {{ ACCOUNT_KIND_LABELS[auth.user.kind] }}
            </AltBadge>
            <span
              v-for="r in rolesAffiches"
              :key="r"
              class="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-700 dark:bg-alt-dark-raised dark:text-altoneo-50"
            >
              {{ ROLE_LABELS[r] }}
            </span>
          </div>
        </div>
      </div>

      <!-- Préférences -->
      <div class="border-b border-slate-100 p-3 dark:border-alt-dark-border">
        <p class="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Préférences</p>
        <p class="mb-1 text-xs text-slate-600 dark:text-alt-dark-muted">Thème</p>
        <div class="mb-3 flex gap-1">
          <button
            v-for="opt in themeOptions"
            :key="opt.value"
            type="button"
            class="flex-1 rounded-md border px-2 py-1 text-xs transition-colors"
            :class="themePreference === opt.value ? actif : inactif"
            :aria-pressed="themePreference === opt.value"
            @click="setTheme(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
        <p class="mb-1 text-xs text-slate-600 dark:text-alt-dark-muted">Taille du texte</p>
        <div class="mb-3 flex gap-1">
          <button
            v-for="opt in fontOptions"
            :key="opt.value"
            type="button"
            class="flex-1 rounded-md border px-2 py-1 text-xs transition-colors"
            :class="fontScale === opt.value ? actif : inactif"
            :aria-pressed="fontScale === opt.value"
            @click="setFontScale(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
        <label class="flex items-center justify-between text-xs text-slate-600 dark:text-alt-dark-muted">
          <span>Contraste élevé</span>
          <AltSwitch :model-value="highContrast" @update:model-value="setHighContrast" />
        </label>
      </div>

      <button
        type="button"
        role="menuitem"
        class="flex w-full items-center gap-2 border-b border-slate-100 px-4 py-2.5 text-left text-sm text-space-indigo dark:border-alt-dark-border dark:text-altoneo-50 pa-survol"
        @click="visite"
      >
        <span aria-hidden="true">🧭</span> Visite guidée &amp; mode démo
      </button>
      <button
        type="button"
        role="menuitem"
        class="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-amaranth pa-survol"
        @click="logout"
      >
        <span aria-hidden="true">⏻</span> {{ decouverte.actif ? 'Quitter la démo' : 'Déconnexion' }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { ACCOUNT_KIND_LABELS, ROLE_LABELS, photoMicrosoft } from '@its/shared';
import { useAuthStore } from '@/stores/auth';
import { useDecouverteStore } from '@/stores/decouverte';
import { useModeDemo } from '@/composables/useModeDemo';
import UserAvatar from '@/components/UserAvatar.vue';
import {
  themePreference,
  highContrast,
  fontScale,
  setTheme,
  setHighContrast,
  setFontScale,
  type ThemePreference,
  type FontScale,
} from '@/lib/preferences';

const auth = useAuthStore();
const router = useRouter();
const decouverte = useDecouverteStore();
const modeDemo = useModeDemo();

const open = ref(false);
// Chargés une fois, à l'arrivée : un lien qui manque ne vaut pas une erreur affichée.
const rootRef = ref<HTMLElement | null>(null);

// `demandeur` est de droit pour tous : l'afficher n'apprendrait rien à personne.
const rolesAffiches = computed(() => auth.roles.filter((r) => r !== 'demandeur'));

// En sombre, le navy plein se fond dans le menu : l'état choisi passe à l'or, comme BasculeSegment.
const actif =
  'border-space-indigo bg-space-indigo text-white dark:border-sunflower-gold dark:bg-sunflower-gold dark:text-space-indigo';
const inactif =
  'border-slate-200 text-slate-600 dark:border-alt-dark-border dark:text-alt-dark-muted pa-survol';

const themeOptions: { value: ThemePreference; label: string }[] = [
  { value: 'clair', label: 'Clair' },
  { value: 'sombre', label: 'Sombre' },
  { value: 'systeme', label: 'Système' },
];
const fontOptions: { value: FontScale; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'confort', label: 'Confort' },
  { value: 'grand', label: 'Grand' },
];

async function visite(): Promise<void> {
  open.value = false;
  await router.push('/decouverte');
}

async function logout(): Promise<void> {
  open.value = false;
  // En démo, « se déconnecter » veut dire sortir de la démo : la vraie session n'a pas bougé.
  if (decouverte.actif) return modeDemo.quitter();
  await auth.logout();
  await router.push({ name: 'login' });
}

function onDocClick(e: MouseEvent): void {
  if (open.value && rootRef.value && !rootRef.value.contains(e.target as Node)) open.value = false;
}
function onKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') open.value = false;
}
onMounted(() => {
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onKey);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick);
  document.removeEventListener('keydown', onKey);
});
</script>
